/**
 * Modelo de dominio compartido entre cliente y servidor.
 * Nada aquí debe importar de `server/` ni de `src/`.
 */

/* ------------------------------------------------------------------ */
/* Usuarios                                                            */
/* ------------------------------------------------------------------ */

export type UserRole = 'user' | 'mod' | 'guardian' | 'admin';

export type ChessTitle =
  | 'GM' | 'WGM' | 'IM' | 'WIM' | 'FM' | 'WFM'
  | 'NM' | 'CM' | 'WCM' | 'WNM' | 'LM' | 'BOT';

export const CHESS_TITLES: ChessTitle[] = [
  'GM', 'WGM', 'IM', 'WIM', 'FM', 'WFM', 'NM', 'CM', 'WCM', 'WNM', 'LM', 'BOT',
];

/** Usuario tal y como se expone públicamente. Nunca incluye email ni hash. */
export interface PublicUser {
  id: string;
  username: string;
  title: ChessTitle | null;
  avatar: string;
  avatarUrl: string | null;
  /** Código ISO 3166-1 alfa-2, en mayúsculas. Ver `shared/countries.ts`. */
  country: string | null;
  bio: string | null;
  /** Nombre real y ciudad o región, opcionales, para la ficha del perfil. */
  realName: string | null;
  location: string | null;
  isBot: boolean;
  isVerified: boolean;
  /** La moderación confirmó uso de motor: no aparece en clasificaciones. */
  engineMarked?: boolean;
  isOnline: boolean;
  createdAt: number;
  lastSeenAt: number;
}

/** Usuario completo, solo para su propio dueño. */
export interface PrivateUser extends PublicUser {
  email: string;
  role: UserRole;
  prefs: UserPrefs;
  /** Con una foto puesta, el busto o emoji de antes; si no, null. */
  avatarFigure: string | null;
  /** Cuenta VIP: entra en Pro-Tactics. */
  isVip: boolean;
  birthDate?: string | null;
  gender?: 'female' | 'male' | 'other' | null;
}

export interface UserStats {
  gamesPlayed: number;
  wins: number;
  losses: number;
  draws: number;
  followers: number;
  following: number;
  puzzleStreak: number;
  bestPuzzleStreak: number;
  puzzlesSolved: number;
  ratedGames: number;
  /** Puntos sumados en todos los torneos jugados. */
  tournamentPoints: number;
  studies: number;
  blogPosts: number;
  /** Partidas marcadas; solo se cuentan para el dueño del perfil. */
  bookmarks: number;
  /** Tiempo jugando partidas en tiempo real y saliendo en UAChess TV, en ms. */
  playTimeMs: number;
  tvTimeMs: number;
}

/** Comunidad a la que pertenece alguien, para la ficha del perfil. */
export interface ProfileCommunity {
  slug: string;
  name: string;
  emblem: string;
  emblemBg: string | null;
  kind: string;
  memberCount: number;
}

export interface UserProfileView {
  user: PublicUser;
  ratings: Record<string, RatingView>;
  stats: UserStats;
  /** Relación del visitante con este perfil. */
  viewerFollows: boolean;
  followsViewer: boolean;
  isSelf: boolean;
  communities: ProfileCommunity[];
  /** Partida en tiempo real que está jugando ahora, si hay alguna. */
  playingGameId: string | null;
}

/* ------------------------------------------------------------------ */
/* Preferencias                                                        */
/* ------------------------------------------------------------------ */

export type PieceAnimationSpeed = 'none' | 'fast' | 'normal' | 'slow';
export type CoordinatesMode = 'none' | 'inside' | 'outside';
export type MoveInputMode = 'both' | 'click' | 'drag';
export type PromotionMode = 'dialog' | 'always-queen';

export interface UserPrefs {
  boardTheme: string;
  pieceSet: string;
  boardSize: number;           // 0.7 – 1.0, fracción del contenedor
  coordinates: CoordinatesMode;
  animationSpeed: PieceAnimationSpeed;
  moveInput: MoveInputMode;
  promotion: PromotionMode;
  premovesEnabled: boolean;
  autoQueenOnPremove: boolean;
  highlightLastMove: boolean;
  showLegalMoves: boolean;
  showDestinationDots: boolean;
  showCaptureRings: boolean;
  showEvalBar: boolean;
  showMoveListInline: boolean;
  blindfold: boolean;
  confirmResign: boolean;
  soundEnabled: boolean;
  soundTheme: string;
  soundVolume: number;         // 0 – 1
  hapticsEnabled: boolean;
  clockTenths: boolean;
  clockSound: boolean;
  language: 'es' | 'en';
  theme: 'dark' | 'light' | 'system';
  zenMode: boolean;
  /** Aceptar jugadores de la casa (motor) si no hay rival humano a tiempo. */
  houseOpponents: boolean;
}

export const DEFAULT_PREFS: UserPrefs = {
  boardTheme: 'wood',
  pieceSet: 'cburnett',
  boardSize: 1,
  coordinates: 'inside',
  animationSpeed: 'normal',
  moveInput: 'both',
  promotion: 'dialog',
  premovesEnabled: true,
  autoQueenOnPremove: true,
  highlightLastMove: true,
  showLegalMoves: true,
  showDestinationDots: true,
  showCaptureRings: true,
  showEvalBar: true,
  showMoveListInline: true,
  blindfold: false,
  confirmResign: true,
  soundEnabled: true,
  soundTheme: 'standard',
  soundVolume: 0.7,
  hapticsEnabled: true,
  clockTenths: true,
  clockSound: true,
  language: 'es',
  theme: 'dark',
  zenMode: false,
  houseOpponents: true,
};

/* ------------------------------------------------------------------ */
/* Ratings (Glicko-2)                                                  */
/* ------------------------------------------------------------------ */

export type RatingCategory =
  | 'ultraBullet' | 'bullet' | 'blitz' | 'rapid' | 'classical' | 'correspondence'
  | 'puzzle'
  | 'chess960' | 'kingOfTheHill' | 'threeCheck' | 'antichess' | 'atomic'
  | 'horde' | 'racingKings' | 'crazyhouse';

export const RATING_CATEGORIES: RatingCategory[] = [
  'ultraBullet', 'bullet', 'blitz', 'rapid', 'classical', 'correspondence', 'puzzle',
  'chess960', 'kingOfTheHill', 'threeCheck', 'antichess', 'atomic',
  'horde', 'racingKings', 'crazyhouse',
];

export interface RatingView {
  category: RatingCategory;
  rating: number;
  deviation: number;
  /** Provisional mientras la desviación sigue alta o hay pocas partidas. */
  provisional: boolean;
  games: number;
  peak: number;
  /** Variación acumulada en los últimos 7 días, para la flecha del perfil. */
  progress: number;
}

/* ------------------------------------------------------------------ */
/* Variantes                                                           */
/* ------------------------------------------------------------------ */

export type Variant =
  | 'standard' | 'chess960' | 'kingOfTheHill' | 'threeCheck'
  | 'antichess' | 'atomic' | 'horde' | 'racingKings' | 'crazyhouse';

export type Speed =
  | 'ultraBullet' | 'bullet' | 'blitz' | 'rapid' | 'classical' | 'correspondence';

/* ------------------------------------------------------------------ */
/* Control de tiempo                                                   */
/* ------------------------------------------------------------------ */

/**
 * Cómo se devuelve tiempo tras cada jugada.
 *  - `fischer`   : se suma el incremento íntegro al terminar de mover.
 *  - `bronstein` : se devuelve lo consumido hasta un máximo del incremento.
 *  - `simple`    : el reloj no corre durante los primeros `delay` segundos.
 *  - `none`      : reloj a la baja sin compensación.
 */
export type ClockMode = 'none' | 'fischer' | 'bronstein' | 'simple';

export interface TimeControl {
  /** Segundos iniciales por jugador. 0 con `kind: 'unlimited'` = sin reloj. */
  initial: number;
  /** Segundos añadidos o devueltos por jugada, según `mode`. */
  increment: number;
  mode: ClockMode;
  /** Solo en correspondencia: días disponibles por jugada. */
  daysPerMove?: number;
  kind: 'realtime' | 'correspondence' | 'unlimited';
  /** Reloj distinto para cada bando (handicap): segundos iniciales de negras. */
  initialBlack?: number;
  incrementBlack?: number;
}

export interface TimeControlPreset {
  id: string;
  label: string;
  speed: Speed;
  tc: TimeControl;
  popular?: boolean;
}

const rt = (initial: number, increment: number, mode: ClockMode = increment > 0 ? 'fischer' : 'none'): TimeControl =>
  ({ initial, increment, mode, kind: 'realtime' });

const corr = (daysPerMove: number): TimeControl =>
  ({ initial: 0, increment: 0, mode: 'none', daysPerMove, kind: 'correspondence' });

/** Catálogo completo de modalidades jugables. */
export const TIME_PRESETS: TimeControlPreset[] = [
  // UltraBullet
  { id: 'ub-15',    label: '¼ + 0',    speed: 'ultraBullet', tc: rt(15, 0) },
  { id: 'ub-30',    label: '½ + 0',    speed: 'ultraBullet', tc: rt(30, 0), popular: true },
  { id: 'ub-30-1',  label: '½ + 1',    speed: 'ultraBullet', tc: rt(30, 1) },
  // Bullet
  { id: 'b-60',     label: '1 + 0',    speed: 'bullet', tc: rt(60, 0), popular: true },
  { id: 'b-60-1',   label: '1 + 1',    speed: 'bullet', tc: rt(60, 1), popular: true },
  { id: 'b-120-1',  label: '2 + 1',    speed: 'bullet', tc: rt(120, 1), popular: true },
  { id: 'b-120-0',  label: '2 + 0',    speed: 'bullet', tc: rt(120, 0) },
  // Blitz
  { id: 'z-180',    label: '3 + 0',    speed: 'blitz', tc: rt(180, 0), popular: true },
  { id: 'z-180-2',  label: '3 + 2',    speed: 'blitz', tc: rt(180, 2), popular: true },
  { id: 'z-300',    label: '5 + 0',    speed: 'blitz', tc: rt(300, 0), popular: true },
  { id: 'z-300-3',  label: '5 + 3',    speed: 'blitz', tc: rt(300, 3), popular: true },
  { id: 'z-300-5',  label: '5 + 5',    speed: 'blitz', tc: rt(300, 5) },
  { id: 'z-180-2b', label: '3 + 2 Bronstein', speed: 'blitz', tc: { initial: 180, increment: 2, mode: 'bronstein', kind: 'realtime' } },
  { id: 'z-300-3d', label: '5 · 3 delay',     speed: 'blitz', tc: { initial: 300, increment: 3, mode: 'simple', kind: 'realtime' } },
  // Rapid
  { id: 'r-600',    label: '10 + 0',   speed: 'rapid', tc: rt(600, 0), popular: true },
  { id: 'r-600-5',  label: '10 + 5',   speed: 'rapid', tc: rt(600, 5), popular: true },
  { id: 'r-900-10', label: '15 + 10',  speed: 'rapid', tc: rt(900, 10), popular: true },
  { id: 'r-1200',   label: '20 + 0',   speed: 'rapid', tc: rt(1200, 0) },
  { id: 'r-1500-10',label: '25 + 10',  speed: 'rapid', tc: rt(1500, 10) },
  { id: 'r-900-10d',label: '15 · 10 delay', speed: 'rapid', tc: { initial: 900, increment: 10, mode: 'simple', kind: 'realtime' } },
  // Clásica
  { id: 'c-1800',   label: '30 + 0',   speed: 'classical', tc: rt(1800, 0), popular: true },
  { id: 'c-1800-20',label: '30 + 20',  speed: 'classical', tc: rt(1800, 20) },
  { id: 'c-2700-30',label: '45 + 30',  speed: 'classical', tc: rt(2700, 30) },
  { id: 'c-3600',   label: '60 + 0',   speed: 'classical', tc: rt(3600, 0) },
  { id: 'c-5400-30',label: '90 + 30',  speed: 'classical', tc: rt(5400, 30) },
  // Correspondencia
  { id: 'x-1d',     label: '1 día/jugada',  speed: 'correspondence', tc: corr(1) },
  { id: 'x-2d',     label: '2 días/jugada', speed: 'correspondence', tc: corr(2), popular: true },
  { id: 'x-3d',     label: '3 días/jugada', speed: 'correspondence', tc: corr(3), popular: true },
  { id: 'x-5d',     label: '5 días/jugada', speed: 'correspondence', tc: corr(5) },
  { id: 'x-7d',     label: '7 días/jugada', speed: 'correspondence', tc: corr(7) },
  { id: 'x-14d',    label: '14 días/jugada',speed: 'correspondence', tc: corr(14) },
  // Sin reloj
  { id: 'inf',      label: 'Sin límite', speed: 'correspondence', tc: { initial: 0, increment: 0, mode: 'none', kind: 'unlimited' } },
];

/**
 * Clasifica un control de tiempo igual que Lichess:
 * duración estimada = inicial + 40 × incremento.
 */
export function speedOf(tc: TimeControl): Speed {
  if (tc.kind === 'unlimited') return 'correspondence';
  if (tc.kind === 'correspondence') return 'correspondence';
  const total = tc.initial + 40 * tc.increment;
  if (total < 30) return 'ultraBullet';
  if (total < 180) return 'bullet';
  if (total < 480) return 'blitz';
  if (total < 1500) return 'rapid';
  return 'classical';
}

/** Etiqueta legible: «5 + 3», «3 · 2 delay», «2 días/jugada», «∞». */
export function formatTimeControl(tc: TimeControl): string {
  if (tc.kind === 'unlimited') return '∞';
  if (tc.kind === 'correspondence') {
    const d = tc.daysPerMove ?? 2;
    return d === 1 ? '1 día/jugada' : `${d} días/jugada`;
  }
  const min = tc.initial % 60 === 0 ? String(tc.initial / 60) : (tc.initial / 60).toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
  const base = tc.initial < 60 ? `${tc.initial}s` : min;
  switch (tc.mode) {
    case 'fischer':   return `${base} + ${tc.increment}`;
    case 'bronstein': return `${base} + ${tc.increment} Bronstein`;
    case 'simple':    return `${base} · ${tc.increment} delay`;
    default:          return `${base} + 0`;
  }
}

export const SPEED_LABELS: Record<Speed, string> = {
  ultraBullet: 'UltraBullet',
  bullet: 'Bullet',
  blitz: 'Blitz',
  rapid: 'Rápida',
  classical: 'Clásica',
  correspondence: 'Correspondencia',
};

export const SPEED_ICONS: Record<Speed, string> = {
  ultraBullet: '🚀',
  bullet: '⚡',
  blitz: '🔥',
  rapid: '⏱️',
  classical: '⏳',
  correspondence: '📮',
};

/* ------------------------------------------------------------------ */
/* Partidas                                                            */
/* ------------------------------------------------------------------ */

export interface GamePlayerView {
  userId: string | null;
  username: string;
  title: ChessTitle | null;
  avatar: string;
  country: string | null;
  rating: number | null;
  provisional: boolean;
  ratingDiff: number | null;
  isBot: boolean;
  botLevel?: number;
}

export type GameStatus =
  | 'created' | 'started'
  | 'mate' | 'resign' | 'stalemate' | 'timeout' | 'draw'
  | 'outoftime' | 'aborted' | 'insufficient' | 'fiftyMove' | 'repetition'
  | 'variantEnd';

export type Winner = 'white' | 'black' | null;

export type GameSource =
  | 'lobby' | 'friend' | 'ai' | 'arena' | 'swiss' | 'simul' | 'import' | 'position' | 'local';

export interface GameView {
  id: string;
  variant: Variant;
  speed: Speed;
  rated: boolean;
  timeControl: TimeControl;
  status: GameStatus;
  winner: Winner;
  source: GameSource;
  white: GamePlayerView;
  black: GamePlayerView;
  initialFen: string;
  /** Jugadas en SAN separadas por espacio. */
  moves: string;
  /** Centisegundos restantes del jugador que acaba de mover, tras cada ply. */
  clockHistory: number[];
  plyCount: number;
  openingEco: string | null;
  openingName: string | null;
  createdAt: number;
  startedAt: number | null;
  endedAt: number | null;
  tournamentId: string | null;
  /** Estado vivo del reloj; solo en partidas en curso. */
  clock?: LiveClock;
}

export interface LiveClock {
  /** Centisegundos restantes. */
  white: number;
  black: number;
  /** A quién le corre el reloj, o null si aún no arrancó. */
  running: 'white' | 'black' | null;
  /** Marca de tiempo del servidor en la que se midió. */
  at: number;
  /** Segundos de delay aún sin consumir en la jugada actual (modo `simple`). */
  delayRemaining?: number;
}

/* ------------------------------------------------------------------ */
/* Social                                                              */
/* ------------------------------------------------------------------ */

export type NotificationType =
  | 'follow' | 'challenge' | 'gameEnd' | 'studyInvite' | 'mention'
  | 'tournamentSoon' | 'privateMessage' | 'teamJoin'
  /** Resolución de una solicitud de título federado. */
  | 'title'
  | 'forumReply' | 'forumMention'
  | 'simulInvite' | 'simulAccepted' | 'simulStart'
  /** Aviso de la moderación y respuesta a una denuncia puesta por ti. */
  | 'warning' | 'reportResolved'
  /** Correspondencia: te toca mover, o se acaba tu plazo. */
  | 'corrTurn' | 'corrDeadline';

export interface NotificationView {
  id: string;
  type: NotificationType;
  payload: Record<string, unknown>;
  read: boolean;
  createdAt: number;
}

/* ------------------------------------------------------------------ */
/* Respuestas de API                                                   */
/* ------------------------------------------------------------------ */

export interface ApiError {
  error: string;
  message: string;
  /** Errores por campo, para formularios. */
  fields?: Record<string, string>;
}

export interface Paginated<T> {
  items: T[];
  nextCursor: string | null;
  total?: number;
}

/* ------------------------------------------------------------------ */
/* Pro-Tactics (solo VIP)                                              */
/* ------------------------------------------------------------------ */

/** Tema de la Enciclopedia de combinaciones, de «a» (destrucción de la defensa) a «j» (doble ataque). */
export type ProTacticsTheme = 'a' | 'b' | 'c' | 'd' | 'e' | 'f' | 'g' | 'h' | 'i' | 'j';
/** 1 = con ataque al rey, 2 = para lograr la igualdad, 3 = otras combinaciones. */
export type ProTacticsGroup = 1 | 2 | 3;

export interface ProTacticsPosition {
  /** Número del diagrama en el libro (1–3001). */
  n: number;
  theme: ProTacticsTheme;
  group: ProTacticsGroup;
  fen: string;
  white: string | null;
  black: string | null;
  site: string | null;
  year: number | null;
  result: string;
  /** Referencia al Informator, si la hay. */
  source: string | null;
  /** Solución del libro: el texto de jugadas del PGN, con variantes. */
  pgn: string;
}

export interface ProTacticsNote {
  name: string;
  text: string;
  createdAt: number;
  updatedAt: number;
}

export interface ProTacticsSummary {
  n: number;
  /** Para la miniatura de la lista. */
  fen: string;
  theme: ProTacticsTheme;
  group: ProTacticsGroup;
  white: string | null;
  black: string | null;
  site: string | null;
  year: number | null;
  noted: boolean;
  noteName: string | null;
}

/* ------------------------------------------------------------------ */
/* Pro-Endgames (solo VIP)                                             */
/* ------------------------------------------------------------------ */

/** Tomo de «Comprehensive Chess Endings» (Averbakh): 1 alfiles y caballos, 2 alfil/caballo y torre contra pieza menor, 3 otros, 4 peones, 5 torres. */
export type ProEndgamesVolume = 1 | 2 | 3 | 4 | 5;

export interface ProEndgamesPosition {
  /** Número del diagrama en esta colección (1–3070). */
  n: number;
  volume: ProEndgamesVolume;
  /** Número del diagrama dentro de su tomo, como en el libro. */
  diagram: number | null;
  fen: string;
  white: string | null;
  black: string | null;
  site: string | null;
  year: number | null;
  result: string;
  /** Página del libro. */
  source: string | null;
  /** Solución del libro: el texto de jugadas del PGN, con variantes y comentarios. */
  pgn: string;
}

export interface ProEndgamesNote {
  name: string;
  text: string;
  createdAt: number;
  updatedAt: number;
}

export interface ProEndgamesSummary {
  n: number;
  /** Para la miniatura de la lista. */
  fen: string;
  volume: ProEndgamesVolume;
  diagram: number | null;
  white: string | null;
  black: string | null;
  site: string | null;
  year: number | null;
  noted: boolean;
  noteName: string | null;
}
